'use client'

/**
 * CountryPicker — searchable country dropdown with the tenant's favourite
 * countries pinned to the top (in the order the dealer set them).
 *
 * Single source for country selection across the product:
 *   - the portal (orders / contacts / settings) imports it via the thin
 *     re-export at apps/portal/src/components/CountryPicker.tsx
 *   - the storefront form renderer uses it for the `country` field type
 *
 * The menu renders into document.body via a portal so it is never clipped by
 * an overflow-hidden ancestor (e.g. a collapsible SectionCard). Flips above the
 * input when there isn't room below. Country data comes from @sifly/shared-utils.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ALL_COUNTRIES, countryName } from '@sifly/shared-utils'

type Props = {
  value: string
  onChange: (code: string) => void
  favorites?: string[]
  placeholder?: string
  /** Trigger-input class. Defaults to the portal's input style; the storefront
   *  form renderer passes its own `inputCls` so the control matches the form. */
  inputClassName?: string
}

const DEFAULT_INPUT_CLASS = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm'

export default function CountryPicker({
  value,
  onChange,
  favorites = ['US', 'CA', 'MX'],
  placeholder = 'Country',
  inputClassName = DEFAULT_INPUT_CLASS,
}: Props) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [menuPos, setMenuPos] = useState<{ left: number; width: number; top?: number; bottom?: number }>({ left: 0, width: 0 })

  // Position the menu against the input. It renders into document.body via a
  // portal so it is never clipped by an overflow-hidden ancestor. Flips above
  // the input when there isn't room below.
  useLayoutEffect(() => {
    if (!open) return
    function reposition() {
      const input = inputRef.current
      if (!input) return
      const r = input.getBoundingClientRect()
      const MENU_MAX = 224 // matches max-h-56
      const spaceBelow = window.innerHeight - r.bottom
      const openUp = spaceBelow < MENU_MAX && r.top > spaceBelow
      setMenuPos({
        left: r.left,
        width: r.width,
        top: openUp ? undefined : r.bottom + 4,
        bottom: openUp ? window.innerHeight - r.top + 4 : undefined,
      })
    }
    reposition()
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
    }
  }, [open])

  // Close on outside click. The menu is portaled out of containerRef, so
  // check it separately.
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      const target = e.target as Node
      if (containerRef.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return
      setOpen(false)
      setQuery('')
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  // Build ordered list: favorites first, divider implied, then rest alphabetically
  const favSet = new Set(favorites)
  const favCountries = favorites
    .map(code => ALL_COUNTRIES.find(c => c.code === code))
    .filter(Boolean) as { code: string; name: string }[]
  const restCountries = ALL_COUNTRIES
    .filter(c => !favSet.has(c.code))
    .sort((a, b) => a.name.localeCompare(b.name))

  const q = query.toLowerCase()
  const filteredFavs = q ? favCountries.filter(c => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q)) : favCountries
  const filteredRest = q ? restCountries.filter(c => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q)) : restCountries

  const displayValue = value ? countryName(value) : ''

  function select(code: string) {
    onChange(code)
    setOpen(false)
    setQuery('')
  }

  return (
    <div ref={containerRef} className="relative">
      <input
        ref={inputRef}
        type="text"
        value={open ? query : displayValue}
        onChange={e => { setQuery(e.target.value); if (!open) setOpen(true) }}
        onFocus={() => { setOpen(true); setQuery('') }}
        placeholder={placeholder}
        className={inputClassName}
      />
      {open && createPortal(
        <div
          ref={menuRef}
          style={{ position: 'fixed', left: menuPos.left, width: menuPos.width, top: menuPos.top, bottom: menuPos.bottom }}
          className="z-50 bg-white border border-gray-200 rounded-xl shadow-lg max-h-56 overflow-y-auto"
        >
          {filteredFavs.length > 0 && (
            <>
              {filteredFavs.map(c => (
                <button
                  key={c.code}
                  onMouseDown={() => select(c.code)}
                  className={`w-full text-left px-3 py-1.5 text-sm hover:bg-[var(--color-surface-muted)] ${value === c.code ? 'bg-[var(--color-selected-soft)] text-[var(--color-text-primary)] font-medium' : 'text-[var(--color-text-primary)]'}`}
                >
                  {c.name} <span className="text-gray-400 text-xs ml-1">{c.code}</span>
                </button>
              ))}
              {filteredRest.length > 0 && (
                <div className="border-t border-gray-100 my-1" />
              )}
            </>
          )}
          {filteredRest.map(c => (
            <button
              key={c.code}
              onMouseDown={() => select(c.code)}
              className={`w-full text-left px-3 py-1.5 text-sm hover:bg-[var(--color-surface-muted)] ${value === c.code ? 'bg-[var(--color-selected-soft)] text-[var(--color-text-primary)] font-medium' : 'text-[var(--color-text-primary)]'}`}
            >
              {c.name} <span className="text-gray-400 text-xs ml-1">{c.code}</span>
            </button>
          ))}
          {filteredFavs.length === 0 && filteredRest.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-400">No countries match</div>
          )}
        </div>,
        document.body,
      )}
    </div>
  )
}
